import { useMemo, useState } from 'react';

import { Table } from '@tanstack/react-table';

import { Button } from '../shadcn/button';
import { ButtonGroup } from '../shadcn/button-group';
import { Input } from '../shadcn/input';

export function TablePagination<T>({
  table,
  plural,
  className,
  variant = 'default',
}: {
  table: Table<T>;
  plural: string;
  className?: string;
  variant?: 'sm' | 'default';
}) {
  const currentPage = table.getState().pagination.pageIndex + 1;
  const [pageString, setPageString] = useState(currentPage.toString());
  const [isEditing, setIsEditing] = useState(false);

  const totalPages = useMemo(
    () => Math.ceil(table.getFilteredRowModel().rows.length / table.getState().pagination.pageSize),
    [table.getFilteredRowModel().rows.length],
  );

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    const target = e.target as HTMLInputElement;

    if (e.key === 'Enter') {
      const pageNumber = parseInt(pageString);

      if (!pageString || isNaN(pageNumber) || pageNumber < 1 || pageNumber > totalPages) {
        setPageString(currentPage.toString());
      } else {
        table.setPageIndex(pageNumber - 1);
      }

      setIsEditing(false);
      target.blur();
    }
  }

  function onBlur() {
    setIsEditing(false);
    setPageString(currentPage.toString());
  }

  function onFocus() {
    setIsEditing(true);
    setPageString(currentPage.toString());
  }

  return (
    <div
      className={`flex items-center ${className} ${variant === 'sm' ? 'text-xs' : 'my-6 w-full justify-between text-sm'}`}
    >
      <div className="mx-2 text-gray-500">
        Showing{' '}
        {table.getFilteredRowModel().rows.length > 0 && (
          <span>
            {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1} to{' '}
            {Math.min(
              (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
              table.getFilteredRowModel().rows.length,
            )}{' '}
            of{' '}
          </span>
        )}
        <span className="font-medium text-gray-600">
          {table.getFilteredRowModel().rows.length} {plural}
        </span>
      </div>

      <div className="flex items-center gap-1.5 text-gray-600">
        <p className="mx-2">
          <span>Page</span>
          <Input
            type="number"
            min={1}
            max={table.getFilteredRowModel().rows.length}
            value={isEditing ? pageString : totalPages === 0 ? 0 : currentPage}
            onChange={(e) => setPageString(e.target.value)}
            disabled={!table.getFilteredRowModel().rows.length || totalPages <= 1}
            onKeyDown={onKeyDown}
            onFocus={onFocus}
            onBlur={onBlur}
            className={`mx-1.5 inline-flex h-auto w-auto px-2 py-1 ${variant === 'sm' ? 'text-xs' : 'text-sm'}`}
          />
          of {totalPages}
        </p>

        <ButtonGroup>
          <Button
            size={variant}
            onClick={(e) => {
              e.preventDefault();
              setPageString(table.getState().pagination.pageIndex.toString());
              table.previousPage();
            }}
            disabled={!table.getCanPreviousPage()}
          >
            Previous
          </Button>
          <Button
            size={variant}
            onClick={(e) => {
              e.preventDefault();
              setPageString((table.getState().pagination.pageIndex + 2).toString());
              table.nextPage();
            }}
            disabled={!table.getCanNextPage()}
          >
            Next
          </Button>
        </ButtonGroup>
      </div>
    </div>
  );
}
